














import Component, { mixins } from "vue-class-component";
import { Prop } from "vue-property-decorator";
import { FileData, InputFileId } from "@scrile/api-provider/dist/api/FileProvider";
import { QuestionaryInfo } from "@scrile/api-provider/dist/api/QuestionaryInfoProvider";
import { TagInfo } from "@scrile/api-provider/dist/api/TagsProvider";
import { InputUserUpdate, User } from "@scrile/api-provider/dist/api/UserProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import WithAuthUser from "@/mixins/WithAuthUser";
import WithFiles from "@/mixins/WithFiles";
import WithUserTags from "@/mixins/WithUserTags";
import { MUTATION_TYPES } from "@/store/const";
import providers from "@/lib/api";
import { getQuestionaryInfoFields } from "@/lib/questionaryFieldsHelper";
import { getProfileUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { FormValues } from "@/types";
import PageProfileEditView from "./components/PageProfileEditView.vue";

@Component({ name: "PageProfileEdit", components: { PageProfileEditView } })
export default class PageProfileEdit extends mixins(WithAuthUser, WithFiles, WithUserTags) {
  @Prop({ type: String, required: true })
  public readonly userId!: string;

  public fetch = true;

  public loading = false;

  public info: Record<string, string> = {};

  public files: FileData[] = [];

  public user: User | UserPublicData | null = null;

  public userTags: string[] = [];

  public systemTags: TagInfo[] = [];

  public get isAuthor(): boolean {
    return this.authUserStrict.id === this.userId || false;
  }

  public get selectedTags(): TagInfo[] {
    return this.systemTags.filter(t => this.userTags.includes(t.id));
  }

  public async getFiles() {
    this.files = await this.filesFind({
      fileIds: [
        {
          subjectId: this.userId,
          type: "avatar",
        },
        {
          subjectId: this.userId,
          type: "background_image",
        },
      ],
    });
  }

  public async getUserData() {
    if (this.isAuthor) {
      this.user = this.authUserStrict;
    } else {
      const r = await providers.UserPublicDataProvider.search({ data: { id: [this.userId] } });
      const user = r.result.find(i => i.id === this.userId);
      if (user) this.user = user;
    }
  }

  public async getData() {
    try {
      this.fetch = true;
      await Promise.all([
        providers.QuestionaryInfoProvider.find({ id: this.userId }).then(
          r => (this.info = r.fields as Record<string, string>)
        ),
        this.getFiles(),
        this.getUserData(),
        config.featureTags
          ? (this.userTagsGetData().then(r => (this.userTags = r)),
            providers.TagsProvider.findAll().then(r => (this.systemTags = r)))
          : undefined,
      ]);
    } finally {
      this.fetch = false;
    }
  }

  public updateUserTags(tags: string[]) {
    this.userTags = tags;
  }

  public async upload(file: FileList, type: string) {
    if (file.length === 0) return;
    const fileId: InputFileId = {
      type,
      subjectId: this.userId,
    };
    const r = await this.filesUpload({ fileId, file: file[0] });
    await this.getFiles();
    if (this.user) {
      const updateUser = {
        ...this.user,
        ...(r.fileId.type === "avatar" ? { avatar: r } : {}),
        ...(r.fileId.type === "background_image" ? { background: r } : {}),
      };
      this.$store.commit(MUTATION_TYPES.SET_USER, updateUser);
      this.user = updateUser;
    }
  }

  public async save(values: FormValues) {
    const { screenName, username, ...questionaryInfoFields } = values;
    const userData: InputUserUpdate = {
      id: this.userId,
      screenName,
      username,
    };
    getQuestionaryInfoFields().forEach(field => {
      if (!questionaryInfoFields[field.name]) return;

      const trimmedValue = questionaryInfoFields[field.name].trim();
      if (trimmedValue && field.fieldMeta?.regExp) {
        if (!field.fieldMeta.appendText) {
          const execResult: RegExpExecArray = field.fieldMeta.regExp.exec(trimmedValue);
          if (execResult[1]) questionaryInfoFields[field.name] = execResult[1];
        } else if (!field.fieldMeta.regExp.test(trimmedValue)) {
          questionaryInfoFields[field.name] = `${field.fieldMeta.appendText}${trimmedValue}`;
        }
        return;
      }
      questionaryInfoFields[field.name] = trimmedValue;
    });
    const questionaryInfo: QuestionaryInfo = {
      fields: {
        ...questionaryInfoFields,
        userId: this.userId,
      },
    };
    this.loading = true;
    await Promise.all([
      providers.QuestionaryInfoProvider.update({ data: questionaryInfo }),
      providers.UserProvider.update({ data: userData }).then(
        r => this.isAuthor && this.$store.commit(MUTATION_TYPES.SET_USER, r)
      ),
    ]).finally(() => {
      this.loading = false;
    });
    this.$router.push(getProfileUrl(this.authUserStrict));
  }

  created() {
    this.getData();
  }
}
